import React, { useState, Component } from 'react';
import { EyeIcon, RefreshCwIcon, AlertCircleIcon, MapPinIcon, CheckCircleIcon, XIcon } from 'lucide-react';
import { useViewMode } from '../context/ViewModeContext';
import { useBillingCart } from '../context/BillingCartContext';
import { ALL_CAMPAIGNS } from '../data/mockData';
import { TopUpModal } from './billing/TopUpModal';
// Success Toast Component
function SuccessToast({
  amount,
  onClose
}: {
  amount: number;
  onClose: () => void;
}) {
  return <div className="fixed top-4 right-4 z-[9999] animate-in slide-in-from-top-2 fade-in duration-300" data-id="element-820">
      <div className="bg-white rounded-xl shadow-2xl border border-green-200 p-4 flex items-center gap-4 min-w-[320px]" data-id="element-821">
        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-822">
          <CheckCircleIcon size={24} className="text-green-600" data-id="element-823" />
        </div>
        <div className="flex-1" data-id="element-824">
          <h4 className="font-semibold text-gray-900" data-id="element-825">Payment Successful!</h4>
          <p className="text-sm text-gray-600" data-id="element-826">
            ₹{amount.toLocaleString()} added to your wallet
          </p>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-id="element-827">
          <XIcon size={18} data-id="element-828" />
        </button>
      </div>
    </div>;
}
export function CampaignList() {
  const {
    viewMode,
    currentLocation,
    isAllLocationsSelected,
    isSingleLocationSelected,
    selectedBrandLocations,
    getSelectedLocationData
  } = useViewMode();
  const {
    walletBalance,
    setWalletBalance
  } = useBillingCart();
  const [isTopUpModalOpen, setIsTopUpModalOpen] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [lastTopUpAmount, setLastTopUpAmount] = useState(0);
  // Filter campaigns based on view mode and selected locations
  const getFilteredCampaigns = () => {
    if (viewMode === 'store' && currentLocation) {
      return ALL_CAMPAIGNS.filter(c => c.locationCode === currentLocation.code);
    }
    if (viewMode === 'brand') {
      if (isSingleLocationSelected) {
        const locationCode = selectedBrandLocations[0];
        return ALL_CAMPAIGNS.filter(c => c.locationCode === locationCode);
      }
      return ALL_CAMPAIGNS;
    }
    return ALL_CAMPAIGNS;
  };
  const handleTopUpConfirm = (amount: number) => {
    // Add amount to wallet balance
    setWalletBalance(walletBalance + amount);
    setLastTopUpAmount(amount);
    setIsTopUpModalOpen(false);
    // Show success toast
    setShowSuccessToast(true);
    // Auto-hide toast after 4 seconds
    setTimeout(() => {
      setShowSuccessToast(false);
    }, 4000);
  };
  const campaigns = getFilteredCampaigns();
  const selectedLocation = getSelectedLocationData();
  const showLocationColumn = viewMode === 'brand' && isAllLocationsSelected;
  return <div data-id="element-829">
      <div className="flex items-center justify-between mb-4" data-id="element-830">
        <h3 className="text-xl font-medium text-gray-800" data-id="element-831">Campaigns List</h3>
        {viewMode === 'brand' && isAllLocationsSelected && <span className="text-sm text-gray-500" data-id="element-832">
            Showing all {campaigns.length} campaigns across locations
          </span>}
        {viewMode === 'brand' && isSingleLocationSelected && selectedLocation && <span className="inline-flex items-center px-3 py-1 bg-amber-50 text-amber-700 rounded-full text-sm font-medium border border-amber-200" data-id="element-833">
              <MapPinIcon size={14} className="mr-1.5" data-id="element-834" />
              {selectedLocation.name} • {campaigns.length} campaigns
            </span>}
        {viewMode === 'store' && currentLocation && <span className="inline-flex items-center px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-sm font-medium" data-id="element-835">
            <MapPinIcon size={14} className="mr-1.5" data-id="element-836" />
            {currentLocation.name} • {campaigns.length} campaigns
          </span>}
      </div>

      <div className="overflow-x-auto" data-id="element-837">
        <table className="min-w-full border-collapse" data-id="element-838">
          <thead data-id="element-839">
            <tr className="bg-blue-50" data-id="element-840">
              {showLocationColumn && <th className="text-left p-3 text-sm font-medium text-blue-500" data-id="element-841">
                  LOCATION
                </th>}
              <th className="text-left p-3 text-sm font-medium text-blue-500" data-id="element-842">
                CAMPAIGN NAME
              </th>
              <th className="text-left p-3 text-sm font-medium text-blue-500" data-id="element-843">
                PRODUCT
              </th>
              <th className="text-left p-3 text-sm font-medium text-blue-500" data-id="element-844">
                PLATFORM
              </th>
              <th className="text-left p-3 text-sm font-medium text-blue-500" data-id="element-845">
                STATUS
              </th>
              <th className="text-left p-3 text-sm font-medium text-blue-500" data-id="element-846">
                BUDGET
              </th>
              <th className="text-left p-3 text-sm font-medium text-blue-500" data-id="element-847">
                SPENDS
              </th>
              <th className="text-left p-3 text-sm font-medium text-blue-500" data-id="element-848">
                LEADS
              </th>
              <th className="text-left p-3 text-sm font-medium text-blue-500" data-id="element-849">
                CPL
              </th>
              <th className="text-left p-3 text-sm font-medium text-blue-500" data-id="element-850">
                ACTIONS
              </th>
            </tr>
          </thead>
          <tbody data-id="element-851">
            {campaigns.map(campaign => <tr key={campaign.id} className="border-b border-gray-200 hover:bg-gray-50 transition-colors" data-id="element-852">
                {showLocationColumn && <td className="p-3" data-id="element-853">
                    <div className="inline-flex items-center px-2.5 py-1 bg-slate-100 rounded-md" data-id="element-854">
                      <MapPinIcon size={12} className="text-slate-500 mr-1.5" data-id="element-855" />
                      <span className="text-xs font-semibold text-slate-700" data-id="element-856">
                        {campaign.locationCode}
                      </span>
                    </div>
                  </td>}
                <td className="p-3 text-sm text-gray-700 font-medium" data-id="element-857">
                  {campaign.name}
                </td>
                <td className="p-3 text-sm text-gray-700" data-id="element-858">
                  {campaign.product}
                </td>
                <td className="p-3 text-sm text-gray-700" data-id="element-859">
                  {campaign.platform === 'Google' ? <div className="flex items-center" data-id="element-860">
                      <GoogleIcon data-id="element-861" />
                      <span className="ml-2" data-id="element-862">Google</span>
                    </div> : <div className="flex items-center" data-id="element-863">
                      <FacebookIcon data-id="element-864" />
                      <span className="ml-2" data-id="element-865">Facebook</span>
                    </div>}
                </td>
                <td className="p-3 text-sm" data-id="element-866">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${campaign.status === 'Active' ? 'bg-green-50 text-green-600' : campaign.status === 'Paused' ? 'bg-orange-50 text-orange-600' : 'bg-gray-100 text-gray-500'}`} data-id="element-867">
                    {campaign.status}
                  </span>
                </td>
                <td className="p-3 text-sm text-gray-700" data-id="element-868">{campaign.budget}</td>
                <td className="p-3 text-sm text-gray-700" data-id="element-869">{campaign.spends}</td>
                <td className="p-3 text-sm text-gray-700 font-medium" data-id="element-870">
                  {campaign.leads}
                </td>
                <td className="p-3 text-sm text-gray-700" data-id="element-871">{campaign.cpl}</td>
                <td className="p-3 text-sm" data-id="element-872">
                  <div className="flex items-center gap-2" data-id="element-873">
                    {viewMode === 'store' && <button onClick={() => setIsTopUpModalOpen(true)} className="text-blue-500 bg-white rounded-md border border-gray-200 px-3 py-1 text-xs hover:bg-blue-50 transition-colors" data-id="element-874">
                        TOP-UP
                      </button>}
                    {campaign.status === 'Active' && <AlertCircleIcon size={16} className="text-amber-500" data-id="element-875" />}
                    <EyeIcon size={16} className="text-gray-400 hover:text-gray-600 cursor-pointer" data-id="element-876" />
                    <RefreshCwIcon size={16} className="text-gray-400 hover:text-gray-600 cursor-pointer" data-id="element-877" />
                  </div>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>

      {/* Top-Up Modal */}
      <TopUpModal isOpen={isTopUpModalOpen} onClose={() => setIsTopUpModalOpen(false)} currentBalance={walletBalance} onConfirm={handleTopUpConfirm} data-id="element-878" />

      {/* Success Toast */}
      {showSuccessToast && <SuccessToast amount={lastTopUpAmount} onClose={() => setShowSuccessToast(false)} data-id="element-879" />}
    </div>;
}
function GoogleIcon() {
  return <div className="flex items-center justify-center rounded-full w-6 h-6 bg-blue-50" data-id="element-880">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-881">
        <path d="M22.5 12.5c0-1.58-.875-2.95-2.148-3.6.154-.435.238-.905.238-1.4 0-2.21-1.71-3.998-3.818-3.998-.47 0-.92.084-1.336.25C14.818 2.415 13.51 1.5 12 1.5s-2.816.917-3.437 2.25c-.415-.165-.866-.25-1.336-.25-2.11 0-3.818 1.79-3.818 4 0 .494.083.964.237 1.4-1.272.65-2.147 2.018-2.147 3.6 0 1.495.782 2.798 1.942 3.486-.02.17-.032.34-.032.514 0 2.21 1.708 4 3.818 4 .47 0 .92-.086 1.335-.25.62 1.334 1.926 2.25 3.437 2.25 1.512 0 2.818-.916 3.437-2.25.415.163.865.248 1.336.248 2.11 0 3.818-1.79 3.818-4 0-.174-.012-.344-.033-.513 1.158-.687 1.943-1.99 1.943-3.484z" fill="#4285F4" data-id="element-882" />
      </svg>
    </div>;
}
function FacebookIcon() {
  return <div className="flex items-center justify-center rounded-full w-6 h-6 bg-blue-50" data-id="element-883">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-884">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" fill="#1877F2" data-id="element-885" />
      </svg>
    </div>;
}